import React, { Fragment, useCallback, useEffect, useMemo, useState } from "react"

import { useTranslation } from "react-i18next"
import { useSelector } from "react-redux"

import CheckIcon from "@mui/icons-material/Check"
import CloseIcon from "@mui/icons-material/Close"
import { Box, Card, CircularProgress, Grid, Link } from "@mui/material"
import { InputText, Snackbar, Tag, Tooltip, Typography } from "@synapse-analytics/synapse-ui"
import _ from "lodash"
import type { MRT_ColumnDef, MRT_PaginationState } from "material-react-table"

import type { RootState } from "../../store/ReduxStore"
import type { TagColor } from "../../types/custom/projects"
import { ExportJobCreateRequest } from "../../types/generated/api/ExportJobCreateRequest"
import type { PaginatedWorkflowSimulationOutputList } from "../../types/generated/api/PaginatedWorkflowSimulationOutputList"
import { TableConfigurationRequest } from "../../types/generated/api/TableConfigurationRequest"
import type { WorkflowSimulationOutput } from "../../types/generated/api/WorkflowSimulationOutput"
import { convertNestedObjectsToFeatureStrings } from "../../utils/modelDetailsHelpers"
import { JsonPopper } from "../data-display/JsonPopper"
import { KonanJsonView } from "../data-display/KonanJsonView"
import { ExportDataDialog } from "../dialogs/ExportDataDialog"
import { BaseTableContainer } from "./BaseTableContainer"
import { TAG_VARIANTS } from "./CONSTANTS"
import { CustomTableHeaders, CustomTableHeadersWithInternalTooltip } from "./CustomTableHeader"

import styles from "./ProjectDecisions.module.scss"

const tagVariants = [...TAG_VARIANTS]

const tagVariantHash: Record<string, TagColor> = {}

type Props = {
  uuid: string
  data: PaginatedWorkflowSimulationOutputList | undefined
  isFetching: boolean
  isLoading: boolean
  pagination: MRT_PaginationState
  setPagination?: React.Dispatch<React.SetStateAction<MRT_PaginationState>>
  filterObject: object
  setFilterObject: React.Dispatch<React.SetStateAction<object>>
}

// TODO:: refactor this and the projectDecisions table to be the same table (...almost)
export function SimulationOutputTable(props: Readonly<Props>): React.ReactElement {
  const { t } = useTranslation("tables")
  const { uuid, data, isFetching, isLoading, pagination, setPagination, filterObject, setFilterObject } = props

  const currentProject = useSelector((state: RootState) => state.project.currentProject)

  const [isExportDialogOpen, setIsExportDialogOpen] = useState<boolean>(false)

  const clearFilter = useCallback(
    (item: string, type: string): void => {
      const tempObject = filterObject
      // eslint-disable-next-line
      // @ts-ignore
      delete tempObject[`${type}__${item.split(".").join("__")}`]

      setFilterObject({
        ...tempObject,
      })
      setPagination?.({
        pageIndex: 0,
        pageSize: 10,
      })
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [filterObject],
  )

  const handleFilterChange = useCallback(
    (value: string | number, title: string, type: string): void => {
      const tempObject = filterObject

      if (!value) {
        clearFilter(title, type)
        return
      }

      // eslint-disable-next-line
      // @ts-ignore
      tempObject[`${type}__${title.split(".").join("__")}`] = value

      setFilterObject({
        ...tempObject,
      })
      setPagination?.({
        pageIndex: 0,
        pageSize: 10,
      })
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [filterObject],
  )

  const debouncedFilter = useMemo(() => {
    return _.debounce(handleFilterChange, 500)
  }, [handleFilterChange])

  // When the component unmounts, the search request could be still in progress.
  //  In this case, an error will occur when the characters state is set.

  const assignTagVariantToReasonValue = (reason: string): void => {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    tagVariantHash[reason] = tagVariants[0]
    tagVariants.splice(0, 1)
  }

  const decisionReason = (rowData: WorkflowSimulationOutput): { text: string; tooltip: string; type: string } => {
    const tooltip = rowData.output?.description
    let text = ""
    let type = currentProject?.category === "workflow" ? "__" : t("simulationOutput.aiEngine")

    if (rowData.output) {
      // getting last node in the satisfied_flow_path
      // to show it's data in the reason column since it's the last node the prediction ran into
      let lastNode = rowData.output?.satisfied_flow_path?.slice(-1)[0]
      type = lastNode?.type
      if (lastNode?.type !== "project") {
        if (lastNode?.type === "loop-start") {
          const lastIteration = lastNode.iterations[Object.keys(lastNode.iterations).length]
          lastNode = lastIteration[lastIteration.length - 1]
        }

        // Adding __ as a fallback for when there isn't any nodes in the satisfied_flow_path to parse the data from
        text = lastNode ? `${lastNode?.type?.charAt(0).toUpperCase() + lastNode?.type?.slice(1)}` : "__"
      }

      type = lastNode?.type
      if (rowData.output.reason) type += `: ${rowData.output.reason}`
    }

    return { text: text, type: type, tooltip: tooltip }
  }

  //get table columns from response
  const tableColumns = useMemo((): { title: string; field: string; type: string }[] => {
    const columnSet = new Set()

    // loop over the list to collect all possible columns
    if (data?.results && data.results.length > 0) {
      for (let i = 0; i < data.results.length; i++) {
        if (data?.results[i]?.features) {
          for (const item in convertNestedObjectsToFeatureStrings({ obj: data?.results[i]?.features })) {
            const adjustedItem = {
              item: item,
              type: "features",
            }
            const stringifiedItem = JSON.stringify(adjustedItem)
            !columnSet.has(stringifiedItem) && columnSet.add(stringifiedItem)
          }

          // adding calculated features to table columns
          for (const item in convertNestedObjectsToFeatureStrings({
            obj: data?.results[i]?.output?.calculated_features,
          })) {
            const adjustedItem = {
              item: item,
              type: "calculated_features",
            }
            const stringifiedItem = JSON.stringify(adjustedItem)
            !columnSet.has(stringifiedItem) && columnSet.add(stringifiedItem)
          }
        }
      }
    }

    return data?.results && data?.results?.length > 0
      ? // eslint-disable-next-line @typescript-eslint/no-explicit-any
        Array.from(columnSet.keys()).map((column: any) => {
          const item = JSON.parse(column)
          return {
            title: item.item,
            field: item.item,
            type: item.type,
            align: "center",
          }
        })
      : []
  }, [data?.results])

  const adjustedData = useMemo((): WorkflowSimulationOutput[] => {
    return tableColumns && tableColumns.length > 0 && data?.results && data?.results.length > 0
      ? data?.results.map((item: WorkflowSimulationOutput) => {
          return { ...item, features: item.features, calculated_features: item.output?.calculated_features }
        })
      : []
  }, [data, tableColumns])

  const adjustedColumns = useMemo((): MRT_ColumnDef<WorkflowSimulationOutput>[] => {
    return [
      {
        header: t("simulationOutput.columns.decision"),
        Header: (
          <CustomTableHeaders
            title={t("simulationOutput.columns.decision")}
            tooltipText={t("simulationOutput.tooltip.decision")}
          />
        ),
        accessorKey: "joint_output",
        Cell: ({ row }) => {
          return (
            <Typography variant={"p"} variantColor={2}>
              {row.original?.output?.output_label ?? "__"}
            </Typography>
          )
        },
      },
      {
        header: t("simulationOutput.columns.feedback"),
        Header: (
          <CustomTableHeaders
            title={t("simulationOutput.columns.feedback")}
            tooltipText={t("simulationOutput.tooltip.feedback")}
          />
        ),
        accessorKey: "feedback",
        Cell: ({ row }) => {
          return (
            <Typography variant={"p"} variantColor={2}>
              {row.original?.feedback ?? "__"}
            </Typography>
          )
        },
      },
      {
        header: t("simulationOutput.columns.reason"),
        accessorKey: "reason_row",
        Header: (
          <CustomTableHeaders
            title={t("simulationOutput.columns.reason")}
            tooltipText={t("simulationOutput.tooltip.reason")}
          />
        ),
        accessorFn: (row) => (decisionReason(row).text ? decisionReason(row).text : "__"),
        Cell: ({ row }) => {
          const reason = decisionReason(row.original)

          // checking if reason is not in the tag variant hash to assign a tag to it
          // else skip and render the tag directly
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          !tagVariantHash[reason.text] && assignTagVariantToReasonValue(reason.text)
          return (
            <Grid container wrap="nowrap" display={"flex"}>
              {reason.type === "__" ? (
                <Typography variant={"p"} color="warning">
                  {reason.type}
                </Typography>
              ) : (
                <Tooltip title={reason.tooltip}>
                  <Tag color={tagVariantHash[reason.text] ?? "gray"} size="small">
                    {reason.type}
                  </Tag>
                </Tooltip>
              )}
            </Grid>
          )
        },
      },
      ...tableColumns.map((item): MRT_ColumnDef<WorkflowSimulationOutput> => {
        const parsedTitle = item.title.split(".")
        const type = item.type

        return {
          ...item,
          // eslint-disable-next-line
          // @ts-ignore
          accessorKey: `${type}.${item.title}`,
          header: item.title,
          Header: (
            <CustomTableHeadersWithInternalTooltip
              title={parsedTitle[parsedTitle.length - 1] as string}
              tooltipText={
                <Fragment>
                  Source: {item.type}
                  <br />
                  Name: {item.title}
                </Fragment>
              }
            />
          ),
          Filter: () => {
            const [value, setValue] = useState<string | number | undefined>()

            return (
              <InputText
                id={`${type}-${item.title}`}
                key={`${type}-${item.title}`}
                hideDescription
                placeholder={t("simulationOutput.filterPlaceholder")}
                fullWidth
                value={value}
                handleChange={(e) => {
                  const value = e.target.value.toString()

                  setValue(e.target.value)
                  debouncedFilter(
                    item.title === "KONAN_TAGS" || value.includes(",")
                      ? (value
                          .split(",")
                          .map((item) => (typeof item === "string" ? item.trim() : item))
                          .join(",") ?? value)
                      : value,
                    item.title,
                    type,
                  )
                }}
                endAdornment={
                  !!value ? (
                    <CloseIcon
                      fontSize="small"
                      onClick={() => {
                        setValue("")
                        clearFilter(item.title, type)
                        ;(document.getElementById(`${type}-${item.title}`) as HTMLInputElement).value = ""
                      }}
                    />
                  ) : undefined
                }
              />
            )
          },
          Cell: ({ row }) => {
            const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null)

            const value = _.get(row.original, `${type}.${item.title}`)

            return Boolean(value) &&
              (typeof value === "object" || (Array.isArray(value) && (value as []).length > 0)) ? (
              <Fragment>
                {Boolean(anchorEl) && (
                  <JsonPopper
                    id={row.original.row_id?.toString()}
                    isOpen={Boolean(anchorEl)}
                    onClose={(): void => {
                      setAnchorEl(null)
                    }}
                    anchorEl={anchorEl}
                    content={value}
                  />
                )}

                {/* TODO:: replace with SUI anchor component in the revamp
                    leaving the styles inline to remember to remove them when revamping */}
                <Link
                  onClick={(e) => {
                    setAnchorEl(e.target as HTMLButtonElement)
                  }}
                  style={{
                    fontFamily: '"inter",sans-serif',
                    fontWeight: "400",
                    fontSize: "14px",
                    lineHeight: "20px",
                    cursor: "pointer",
                  }}
                >
                  {t("simulationOutput.viewLink")}
                </Link>
              </Fragment>
            ) : typeof value === "boolean" ? (
              value ? (
                <CheckIcon style={{ fontSize: "1.1rem" }} />
              ) : (
                <CloseIcon style={{ fontSize: "1.1rem" }} />
              )
            ) : (
              <Typography variant={"p"}>
                {Array.isArray(value) && (value as []).length === 0 ? "__" : ((value ?? "__") as string)}
              </Typography>
            )
          },
        }
      }),
    ]
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableColumns])

  useEffect(() => {
    return () => {
      debouncedFilter.cancel()
    }
  }, [debouncedFilter])

  return (
    <Fragment>
      {isExportDialogOpen && (
        <ExportDataDialog
          onClose={() => setIsExportDialogOpen(false)}
          jobType={ExportJobCreateRequest.type.SIMULATION_PREDICTIONS}
          simulation_job={uuid}
        />
      )}

      {!isLoading ? (
        <BaseTableContainer
          id="Simulation-Report-table"
          title={t("simulationOutput.title")}
          columns={adjustedColumns as MRT_ColumnDef[]}
          data={adjustedData}
          source={TableConfigurationRequest.table.SIMULATION_OUTPUT}
          isLoading={isFetching}
          manualPagination
          rowCount={data?.count}
          setPagination={setPagination}
          pagination={pagination}
          clearAllFiltersFn={Object.keys(filterObject).length > 0 ? () => setFilterObject({}) : undefined}
          onExportClick={() => setIsExportDialogOpen(true)}
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          detailPanel={(row: any) => {
            return (
              <Box p={2} position={"sticky"} left={0}>
                {row.original?.output?.satisfied_flow_path ? (
                  <Grid container spacing={2}>
                    {Boolean(row.original?.output?.satisfied_flow_path) && (
                      <Grid item xs={12}>
                        <Typography variant="p" gutterBottom>
                          {t("simulationOutput.workflowPath")}
                        </Typography>
                        <KonanJsonView src={row.original?.output?.satisfied_flow_path} />
                      </Grid>
                    )}
                  </Grid>
                ) : (
                  <Snackbar severity={"warning"} fullWidth>
                    {t("simulationOutput.noWorkflowPath")}
                  </Snackbar>
                )}
              </Box>
            )
          }}
        />
      ) : (
        <Card className="card-box-shadow" style={{ height: "300px", display: "flex" }}>
          <Grid container className={styles.emptyStateRoot}>
            <Grid item xs={12} className={styles.emptyStateItem}>
              <CircularProgress size={20} color="primary" />
            </Grid>
            <Grid item xs={12} className={styles.emptyStateItem}>
              <Typography variant={"p"}>{t("simulationOutput.loading")}</Typography>
            </Grid>
          </Grid>
        </Card>
      )}
    </Fragment>
  )
}
